import React, { useState, useEffect } from 'react';
import ModalWrapper from '../../../../../../Components/CustomModal/ModalWrapper';
import { Dropdown } from 'primereact/dropdown';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { V_URL } from '../../../../../../BaseUrl';
import toast from 'react-hot-toast';
import { getUnit } from '../../../../../../Store/Store/StoreMaster/Unit/Unit';
import { getCategory } from '../../../../../../Store/Store/StoreMaster/Category/Category';
import { getLocation } from '../../../../../../Store/Store/StoreMaster/InventoryLocation/Location';
import { getItem } from '../../../../../../Store/Store/Item/Item';

const AddItemModal = ({ isOpen, onClose, onItemCreated }) => {
  const dispatch = useDispatch();
  const categoryData = useSelector((state) => state?.getCategory?.user?.data) || [];
  const unitData = useSelector((state) => state?.getUnit?.user?.data) || [];
  const locationData = useSelector((state) => state?.getLocation?.user?.data) || [];

  const initialFormState = {
    name: '',
    unit: '',
    category: '',
    hsn_code: '',
    gst_percentage: '',
    location: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormState);
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getCategory());
      dispatch(getUnit());
      dispatch(getLocation());
    }
  }, [isOpen, dispatch]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Item name is required';
    if (!formData.unit) newErrors.unit = 'Unit is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.hsn_code.trim()) newErrors.hsn_code = 'HSN code is required';
    if (formData.gst_percentage === '') newErrors.gst_percentage = 'GST is required';
    if (!formData.location) newErrors.location = 'Location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setDisable(true);
    try {
      const bodyFormData = new URLSearchParams();
      bodyFormData.append('firm_id', localStorage.getItem('PAY_USER_FIRM_ID'));
      bodyFormData.append('year_id', localStorage.getItem('PAY_USER_YEAR_ID'));
      bodyFormData.append('name', formData.name.trim());
      bodyFormData.append('unit', formData.unit);
      bodyFormData.append('category', formData.category);
      bodyFormData.append('hsn_code', formData.hsn_code.trim());
      bodyFormData.append('gst_percentage', parseFloat(formData.gst_percentage) || 0);
      bodyFormData.append('location', formData.location);

      const resp = await axios.post(`${V_URL}/user/manage-item`, bodyFormData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${localStorage.getItem('PAY_USER_TOKEN')}`,
        },
      });

      if (resp.data.success) {
        toast.success(resp.data.message || 'Item created successfully');
        dispatch(getItem({ is_main: true }))
        onItemCreated(resp.data.data);
        onClose();
      } else {
        toast.error(resp.data.message || 'Failed to create item');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Something went wrong');
    } finally {
      setDisable(false);
    }
  };

  return (
    <ModalWrapper
      title="Add New Item"
      isShown={isOpen}
      hide={onClose}
      footer={false}
      customSize="modal-lg"
    >
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-lg-6 col-md-12 col-12">
            <div className="input-block local-forms">
              <label>Item Name <span className="login-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter item name"
              />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
          </div>

          <div className="col-lg-6 col-md-12 col-12">
            <div className="input-block local-forms custom-dropdown">
              <label>Unit <span className="login-danger">*</span></label>
              <Dropdown
                value={formData.unit}
                options={unitData.map(e => ({ label: e.name, value: e._id }))}
                onChange={(e) => handleDropdownChange('unit', e.value)}
                placeholder="Select Unit"
                filter
                className={`w-100 ${errors.unit ? 'p-invalid' : ''}`}
                enforceFocus={false}
                appendTo="self"
              />
              {errors.unit && <small className="p-error">{errors.unit}</small>}
            </div>
          </div>

          <div className="col-lg-6 col-md-12 col-12">
            <div className="input-block local-forms custom-dropdown">
              <label>Category <span className="login-danger">*</span></label>
              <Dropdown
                value={formData.category}
                options={categoryData.map(e => ({ label: e.name, value: e._id }))}
                onChange={(e) => handleDropdownChange('category', e.value)}
                placeholder="Select Category"
                filter
                className={`w-100 ${errors.category ? 'p-invalid' : ''}`}
                enforceFocus={false}
                appendTo="self"
              />
              {errors.category && <small className="p-error">{errors.category}</small>}
            </div>
          </div>

          <div className="col-lg-6 col-md-12 col-12">
            <div className="input-block local-forms custom-dropdown">
              <label>HSN Code <span className="login-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${errors.hsn_code ? 'is-invalid' : ''}`}
                name="hsn_code"
                value={formData.hsn_code}
                onChange={handleChange}
                placeholder="Enter HSN code"
              />
              {errors.hsn_code && <div className="invalid-feedback">{errors.hsn_code}</div>}
            </div>
          </div>

          <div className="col-lg-6 col-md-12 col-12">
            <div className="input-block local-forms ">
              <label>GST (%) <span className="login-danger">*</span></label>
              <input
                type="number"
                className={`form-control ${errors.gst_percentage ? 'is-invalid' : ''}`}
                name="gst_percentage"
                value={formData.gst_percentage}
                onChange={handleChange}
                placeholder="Enter GST percentage"
                step="0.01"
              />
              {errors.gst_percentage && <div className="invalid-feedback">{errors.gst_percentage}</div>}
            </div>
          </div>

          <div className="col-lg-6 col-md-12 col-12">
            <div className="input-block local-forms custom-dropdown">
              <label>Location <span className="login-danger">*</span></label>
              <Dropdown
                value={formData.location}
                options={locationData.map(e => ({ label: e.name, value: e._id }))}
                onChange={(e) => handleDropdownChange('location', e.value)}
                placeholder="Select Location"
                filter
                className={`w-100 ${errors.location ? 'p-invalid' : ''}`}
                enforceFocus={false}
                appendTo="self"
              />
              {errors.location && <small className="p-error">{errors.location}</small>}
            </div>
          </div>
        </div>

        <div className="col-12 text-end mt-3">
          <button type="button" className="btn btn-secondary me-2" onClick={onClose} disabled={disable}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={disable}>
            {disable ? 'Creating...' : 'Create Item'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default AddItemModal;
